import { authService } from '@/modules/auth/services/authService';

const BASE_URL = window.pov_config.VUE_APP_API_URL_GATEWAY;
const STREAM_ROUTE = '/v3/ai/aiagents/chat/message/stream';
const ABORT_ROUTE = '/v3/ai/aiagents/chat/conversations/abort';
const PREWARM_BUSINESS_CONTEXT_ROUTE = '/v3/ai/aiagents/chat/business-context/prewarm';

const getAuthHeaders = async (forceRefresh = false) => {
  const tokenResult = await authService.getToken(forceRefresh);
  if (!tokenResult?.success || !tokenResult?.jwt) {
    throw new Error('AUTH_FAILED');
  }
  return {
    'Content-Type': 'application/json',
    'x-app-name': 'Frontage-pov',
    Authorization: `Bearer ${tokenResult.jwt}`,
  };
};

const parseSseBlock = (block) => {
  let eventType = null;
  const dataLines = [];

  block.split('\n').forEach((line) => {
    if (line.startsWith('event: ')) {
      eventType = line.slice(7).trim();
    } else if (line.startsWith('data: ')) {
      dataLines.push(line.slice(6));
    } else if (line === 'data:') {
      dataLines.push('');
    }
  });

  if (!eventType || dataLines.length === 0) return null;
  return { type: eventType, text: dataLines.join('\n') };
};

const doStreamRequest = async (headers, payload, signal) => fetch(`${BASE_URL}${STREAM_ROUTE}`, {
  method: 'POST',
  headers,
  body: JSON.stringify(payload),
  signal,
});

/**
 * POST /chat/message/stream — same for a new user message or for HITL (omit `message`, pass `confirmed`).
 * Pass an AbortSignal via `signal` to support stop-streaming.
 */
const streamMessage = async (payload, onEvent, signal) => {
  let headers = await getAuthHeaders();
  let response = await doStreamRequest(headers, payload, signal);

  if (response.status === 401) {
    headers = await getAuthHeaders(true);
    response = await doStreamRequest(headers, payload, signal);
  }

  if (!response.ok) {
    throw new Error(`Stream request failed: ${response.status}`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  const processChunk = (buffer) => reader.read().then(({ done, value }) => {
    if (done) return Promise.resolve();
    const newBuffer = buffer + decoder.decode(value, { stream: true });
    const blocks = newBuffer.split('\n\n');
    const remaining = blocks.pop();
    blocks
      .filter((block) => !block.startsWith(':'))
      .forEach((block) => {
        const event = parseSseBlock(block);
        if (event) onEvent(event);
      });
    return processChunk(remaining);
  });

  await processChunk('');
};

const abortStream = async (conversationId) => {
  try {
    const headers = await getAuthHeaders();
    await fetch(`${BASE_URL}${ABORT_ROUTE}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ conversation_id: conversationId }),
    });
  } catch { /* best-effort — client-side abort already handled */ }
};

const prewarmBusinessContext = () => getAuthHeaders()
  .then((headers) => fetch(`${BASE_URL}${PREWARM_BUSINESS_CONTEXT_ROUTE}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({}),
  }))
  .catch(() => {});

export default {
  streamMessage,
  abortStream,
  prewarmBusinessContext,
};
